import React, {useEffect, useMemo, useState} from 'react'
import {Address, Client, formatAddress, getAddressLink, WorkEvent, WorkOrder, WorkOrderStatus} from "../types";
import {getClients, getEvents} from "../api/dataService";
import {Link, Navigate, useNavigate} from "react-router-dom";
import {ColumnDef, Row} from "@tanstack/react-table";
import {
    checkIsAllowed,
    getDateFromToday,
    getDateTimeString,
    getTimeString,
    isLoadingSpinner,
    isNightShift
} from "../util/Helpers";
import SearchableTable from "./SearchableTable";
import {Row as BRow, Col, Pagination, ButtonGroup, Button, Modal} from "react-bootstrap";
import {addDays} from "date-fns";
import DatePicker from "react-datepicker";
import {useSelector} from "react-redux";
import {RootState} from "../redux/reduxTypes";
import CreateEvent from "./CreateEvent";
import ElevatorBadge from "./ElevatorBadge";
import {endDateFilter, startDateFilter} from "../util/CustomFilters";


const parentStyle = {
    padding: 0,
}

// Roles permitted to open a work order. Mirrors the roles on the /workorders/:id
// route in index.tsx - keep the two in sync so the link is never rendered for a
// user who would just be bounced back to /home.
const WORK_ORDER_VIEW_ROLES = [
    'Ownership_Group',
    'Management',
    'Sales',
    'Office_Admin',
    'Dispatch',
    'Driver',
    'Supervisor',
]

// The dispatch calendar only lists dispatchable events, which always carry a
// work order - the empty string is a defensive fallback only.
const getWorkOrderId = (event: WorkEvent) => {
    const wo = event.work_order
    if (!wo) return ''
    return typeof wo === 'string' ? wo : String(wo.id)
}

// Only events attached to a work order belong on the dispatch calendar.
// Quote-only events (including rejected/deleted quotes) are not dispatchable
// work - a quote's events gain their work_order when the quote is accepted.
// Note that accepting a quote does not clear event.quote, so the presence of a
// work_order is the reliable test, not the absence of a quote.
const isDispatchable = (event: WorkEvent) => {
    if (event.status === 'deleted') return false
    if (!event.work_order) return false
    if (typeof event.work_order !== 'string'
        && event.work_order.workorder_status === WorkOrderStatus.deleted) return false
    return true
}

interface DispatchCalendarProps {
    after_date?: string;
    before_date?: string;
}

const DispatchCalendar: React.FC<DispatchCalendarProps> = (props) => {
    const user = useSelector((state: RootState) => state.user)
    const [eventList, setEventList] = useState<WorkEvent[]>([])
    const [filteredEvents, setFilteredEvents] = useState<WorkEvent[]>([])
    const [selectedDate, setSelectedDate] = useState(new Date())
    const [isLoading, setIsLoading] = useState(true)
    const [eventToUpdate, setEventToUpdate] = useState<Row<WorkEvent>>()
    const [showCreateModal, setShowCreateModal] = useState(false)
    const [clientMap, setClientMap] = useState<{[key: string]: string}>({})

    const navigate = useNavigate()

    useEffect(() => {
        const after_param = props.after_date? new Date(props.after_date): getDateFromToday(0,-1,0)
        const before_param = props.before_date ? new Date(props.before_date) : getDateFromToday(0,1,0)

        // Update selectedDate when after_date prop changes.
        // Parse as local date (not UTC) to avoid timezone-offset issues where
        // new Date("2026-03-04") is interpreted as UTC midnight and becomes the
        // previous day in timezones behind UTC.
        if (props.after_date) {
            const [year, month, day] = props.after_date.split('-').map(Number);
            setSelectedDate(new Date(year, month - 1, day));
        }

        Promise.all([
            getEvents(undefined, `?target_date=${props.after_date}`),
            getClients()
        ])
            .then(([eventResponse, clientResponse]) => {
                if('redirectTo' in eventResponse){
                    return(<Navigate to={eventResponse.redirectTo}/>)
                }

                if(Array.isArray(eventResponse) && Array.isArray(clientResponse)){
                    // Create a mapping of client IDs to client names
                    const clientMapping: {[key: string]: string} = {};
                    clientResponse.forEach((client: Client) => {
                        clientMapping[client.id] = client.client_name;
                    });

                    // Set the client mapping for use in the column definition
                    setClientMap(clientMapping);

                    // Filter events and set to state
                    setEventList(eventResponse.filter(isDispatchable));
                }

                setIsLoading(false);
            })
            .catch(error => {
                console.error("Error fetching data:", error);
                setIsLoading(false);
            });
    }, [props.after_date]);

    useEffect(() => {
        const currentFilter = selectedDate.toLocaleDateString()
        const filteredData = eventList.filter((item) => {
            const startDate = new Date(item.start_date);
            const endDate = new Date(item.end_date);
            const selectedDateObj = new Date(selectedDate);

            // Reset hours to compare dates only
            startDate.setHours(0, 0, 0, 0);
            endDate.setHours(0, 0, 0, 0);
            selectedDateObj.setHours(0, 0, 0, 0);

            // Show events that:
            // 1. Start on the selected date, OR
            // 2. End on the selected date, OR
            // 3. Span across the selected date (start before and end after)
            return (
                startDate.getTime() === selectedDateObj.getTime() || 
                endDate.getTime() === selectedDateObj.getTime() ||
                (startDate.getTime() <= selectedDateObj.getTime() && endDate.getTime() >= selectedDateObj.getTime())
            );
        })
        .sort((a, b) => new Date (a.start_date).getTime() - new Date(b.start_date).getTime())

        setFilteredEvents(filteredData);

        // Log for debugging
        // console.log("Filtered events for date:", currentFilter, filteredData.length);
    }, [selectedDate, eventList]);


    const columns = useMemo<ColumnDef<WorkEvent, any>[]>(
        () => [
            {
                // Accessor is a string on purpose: a numeric value makes tanstack
                // resolve the 'auto' filter to inNumberRange, which expects a
                // [min, max] pair and silently drops the single value the filter
                // dropdown sends.
                accessorFn: row => getWorkOrderId(row),
                id: 'work_order',
                header: 'Work Order',
                enableColumnFilter: true,
                filterFn: 'equalsString',
                cell: ({row}) => {
                    const workOrderId = getWorkOrderId(row.original)
                    if (!workOrderId) return <p>No Work Order</p>

                    return checkIsAllowed(user.groups, WORK_ORDER_VIEW_ROLES) ?
                        (<Link to={`/workorders/${workOrderId}`}>WO #{workOrderId}</Link>) :
                        (<span>WO #{workOrderId}</span>)
                }
            },
            {
                accessorFn: row => {
                    if (row.work_order && typeof row.work_order !== 'string') {
                        const jobsiteClientId = row.work_order.jobsite_client;
                        return clientMap[jobsiteClientId] || jobsiteClientId || 'No Client';
                    }
                    return 'No Client';
                },
                id: 'jobsite_client',
                header: 'Client',
                enableColumnFilter: true
            },
            {
                accessorFn: row => `${row.event_name} ${row.event_description? `- ${row.event_description}`:''}`,
                id: 'event_name_desc',
                header: 'Description',
                enableColumnFilter: false,
                cell: ({row}) => (
                    <span>
                        {row.original.event_name}{row.original.event_description ? ` - ${row.original.event_description}` : ''}
                        {row.original.elevator_event && <ElevatorBadge />}
                    </span>
                )
            },
            {
                id: 'start_address',
                header:'Origin Address',
                enableColumnFilter: false,
                cell: ({row}) => {
                    if(row.original.origin_address){
                        return (
                            <a href={getAddressLink(row.original.origin_address as Address)}>{formatAddress(row.original.origin_address as Address)}</a>
                        )
                    }else{
                        return <p>No Address</p>
                    }
                },
                accessorFn: row => row.origin_address ? formatAddress(row.origin_address as Address) : 'No Address',
            },
            {
                id: 'end_address',
                header:'Destination Address',
                enableColumnFilter: false,
                cell: ({row}) => {
                    if(row.original.destination_address){
                        return (
                            <a href={getAddressLink(row.original.destination_address as Address)}>{formatAddress(row.original.destination_address as Address)}</a>
                        )
                    }else{
                        return <p>No Address</p>
                    }
                },
                accessorFn: row => row.destination_address ? formatAddress(row.destination_address as Address) : 'No Address',
            },
            {
                accessorFn: row => `${getDateTimeString(row.start_date)}`,
                id: 'start_date',
                header:'Start Date',
                enableColumnFilter: true,
                filterFn: startDateFilter
            },
            {
                accessorFn: row => `${getDateTimeString(row.end_date)}`,
                id: 'end_date',
                header: 'End Date',
                enableColumnFilter: true,
                filterFn: endDateFilter
            },
            {
                id: 'employees',
                header: 'Employees',
                enableColumnFilter: true,
                cell: ({row}) => {
                    const assignedList = row.original.employeeuser_set?.map(employee => employee.first_name)
                        .join(', ');
                    if(assignedList){
                        return (
                            <p>{assignedList}</p>
                        )
                    }else{
                        return <p className={'text-danger'}>No Employees Assigned</p>
                    }
                },
                filterFn:'arrIncludesSome',
                accessorFn: row => {
                    const assigned_emp = row.employeeuser_set?.map(employee => `${employee.first_name} ${employee.last_name}`).join(', ')
                    return assigned_emp ? assigned_emp : 'No Employees Assigned'
                }
            },
            {
                id: 'supervisor',
                header: 'Supervisor',
                enableColumnFilter: true,
                cell: ({row}) => {
                    if(row.original.supervisor){
                        return (
                            `${row.original.supervisor.first_name} ${row.original.supervisor.last_name}`
                        )
                    }else{
                        return <p className={'text-danger'}>None</p>
                    }
                },
                filterFn:'arrIncludesSome',
                accessorFn: row => row.supervisor?`${row.supervisor.first_name} ${row.supervisor.last_name}` : "None"
            },
            {
                id: 'elevator_event',
                header: 'Event Type',
                enableColumnFilter: true,
                accessorFn: row => row.elevator_event ? 'Elevator' : 'Standard',
            },
            {
                id: 'actions',
                header:'Actions',
                cell: ({row}) => (
                    <div className="d-flex justify-content-center">
                        <ButtonGroup className={'btn-group-sm'}>
                            <Button className={'btn-primary'} onClick={() => navigate(`/events/${row.original.id}`)}>View</Button>
                            {checkIsAllowed(user.groups, ['Ownership_Group', 'Management', 'Office_Admin', 'Dispatch',])?
                                (<>
                                    <Button className={'btn-secondary'} onClick={() => handleOpenDetails(row)}>Update</Button>
                                </>) : (<></>)
                            }
                        </ButtonGroup>
                    </div>
                )
            }
        ],
        [props.after_date, clientMap, user.groups]
    )

    const handleOpenDetails = (row: Row<WorkEvent>) => {
        setEventToUpdate(row)
        setShowCreateModal(true)
    }
    const handleCloseCreate = () => {
        setShowCreateModal(false)
        // Delay clearing eventToUpdate until the modal has faded out, so it does
        // not flip to its "Create New Event" state on the way.
        setTimeout(() => {
            setEventToUpdate(undefined)
        }, 100)
    }
    const handleUpdate = async (updated_obj?: WorkEvent, assignedEmployeeList?: string[], assignedResourcesList?: any[]) => {
        if(!updated_obj) return

        // The update response returns relations as bare ids (work_order: 40018),
        // but these columns read into them as nested objects - the Client column
        // needs work_order.jobsite_client. Refetch the row so it has the same shape
        // the list was loaded with, rather than trying to reconstruct it.
        let replacement = updated_obj
        try {
            const refreshed = await getEvents(updated_obj.id.toString())
            if(refreshed && !Array.isArray(refreshed) && !('redirectTo' in refreshed)){
                replacement = refreshed
            }
        } catch (error) {
            // Fall back to the update response: some columns will show less detail
            // until the next load, but the row still reflects the edit.
            console.error("Error refreshing updated event:", error)
        }

        // Match on id, not on the table row's index - rows are indexed against the
        // filtered and sorted list, so using that index here would remove a
        // different event from eventList than the one that was edited.
        setEventList(prevList => {
            const index = prevList.findIndex(item => String(item.id) === String(replacement.id))
            if(index === -1) return prevList

            const updatedList = [...prevList]
            // An edit can take the event off the calendar entirely (deleted, or its
            // work order removed), matching the filter applied on load.
            if(!isDispatchable(replacement)){
                updatedList.splice(index, 1)
            } else {
                updatedList[index] = replacement
            }
            return updatedList
        })
        // filteredEvents is recomputed by the effect watching [selectedDate, eventList].
    }

    if (isLoading) {
        return (isLoadingSpinner());
    }

    return (
        <div style={parentStyle}>
            <SearchableTable columns={columns} data={filteredEvents} globalSearch={false} hiddenColumnIds={['elevator_event']}/>
            <Modal show={showCreateModal} onHide={handleCloseCreate} size={"lg"}>
                <Modal.Header closeButton>
                    <Modal.Title>{eventToUpdate? `Update Event ${eventToUpdate.original.id}`: "Create New Event"}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <CreateEvent
                        closeModal={handleCloseCreate}
                        eventToUpdate={eventToUpdate? eventToUpdate.original:undefined}
                        onUpdate={handleUpdate}
                        isQuoting={false}
                        showCrew={true}
                    />
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseCreate}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    )
}




export default DispatchCalendar
